import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatRelative, repoLabel } from '../lib/format';
import type { ReviewSummary, Severity } from '../types/api';
import { SEVERITIES } from '../types/api';
import { DemoBadge, SEV_DOT, StatusBadge } from './ui/Badges';

export function FindingCountsInline({ counts, total }: { counts: Record<Severity, number>; total: number }) {
  if (total === 0) return <span className="text-faint">0</span>;
  return (
    <span className="inline-flex items-center gap-2.5" aria-label={`${total} findings`}>
      {SEVERITIES.filter((s) => counts[s] > 0).map((s) => (
        <span key={s} className="inline-flex items-center gap-1 tabular-nums" title={`${counts[s]} ${s}`}>
          <span className={`h-2 w-2 rounded-full ${SEV_DOT[s]}`} aria-hidden />
          {counts[s]}
        </span>
      ))}
    </span>
  );
}

/** Responsive review list: a table on wide screens, stacked cards on small ones. */
export function ReviewTable({ reviews }: { reviews: ReviewSummary[] }) {
  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full">
          <thead className="border-b border-dashed border-line">
            <tr>
              <th className="table-head">Pull Request</th>
              <th className="table-head">Status</th>
              <th className="table-head">Findings</th>
              <th className="table-head">Reviewed</th>
              <th className="table-head sr-only">Links</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line/60">
            {reviews.map((r) => (
              <tr key={r.id} className="transition-colors hover:bg-raised/50">
                <td className="table-cell max-w-md">
                  <Link to={`/app/reviews/${r.id}`} className="block font-medium hover:text-chalk-yellow">
                    <span className="line-clamp-1">{r.prTitle}</span>
                  </Link>
                  <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-muted">
                    <span className="min-w-0 truncate font-mono" title={`${repoLabel(r)}#${r.prNumber}`}>
                      {repoLabel(r)}#{r.prNumber}
                    </span>
                    {r.source === 'demo' && <DemoBadge />}
                    {r.trigger === 'webhook' && <span className="text-faint">· via webhook</span>}
                  </div>
                </td>
                <td className="table-cell">
                  <StatusBadge status={r.status} />
                </td>
                <td className="table-cell">
                  {r.status === 'completed' ? <FindingCountsInline counts={r.findingCounts} total={r.findingTotal} /> : <span className="text-faint">—</span>}
                </td>
                <td className="table-cell whitespace-nowrap text-muted" title={new Date(r.createdAt).toLocaleString()}>
                  {formatRelative(r.createdAt)}
                </td>
                <td className="table-cell text-right">
                  <a
                    href={r.prUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex text-faint hover:text-ink"
                    aria-label={`Open ${repoLabel(r)}#${r.prNumber} on GitHub`}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="divide-y divide-line md:hidden">
        {reviews.map((r) => (
          <li key={r.id}>
            <Link to={`/app/reviews/${r.id}`} className="block px-4 py-3 hover:bg-raised/50">
              <div className="flex items-start justify-between gap-3">
                <p className="line-clamp-2 text-sm font-medium">{r.prTitle}</p>
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
                <span className="min-w-0 truncate font-mono" title={`${repoLabel(r)}#${r.prNumber}`}>
                  {repoLabel(r)}#{r.prNumber}
                </span>
                {r.source === 'demo' && <DemoBadge />}
                <span>· {formatRelative(r.createdAt)}</span>
                {r.status === 'completed' && <FindingCountsInline counts={r.findingCounts} total={r.findingTotal} />}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
